import {useMemo, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {useProductLine} from '@progyny/hooks/useProductLine'
import BirthAssignedSex from '@progyny/pages/SetUpProfile/BirthAssignedSex'
import Complete from '@progyny/pages/SetUpProfile/Complete'
import FamilyFormingJourney from '@progyny/pages/SetUpProfile/FamilyFormingJourney'
import GenderIdentity from '@progyny/pages/SetUpProfile/GenderIdentity'
import LastMenstrual from '@progyny/pages/SetUpProfile/LastMenstrual'
import MenopauseSymptoms from '@progyny/pages/SetUpProfile/MenopauseSymptoms'
import MenstrualPeriod from '@progyny/pages/SetUpProfile/MenstrualPeriod'
import PregnancyDate from '@progyny/pages/SetUpProfile/PregnancyDate'
import PregnancyInfo from '@progyny/pages/SetUpProfile/PregnancyInfo'
import RelationshipDynamic from '@progyny/pages/SetUpProfile/RelationshipDynamic'
import {
  SetupProfileRoutes,
  SetupProfileSubPropsSubRoute,
} from '@progyny/pages/SetUpProfile/types'
import {useMeStore} from '@progyny/store/meStore'
import {
  FERTILITY,
  MENOPAUSE,
  PREGNANCY_AND_POSTPARTUM,
} from '@progyny/utils/constants'

const useSetupProfileRoutes = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const isInConservativeCountry = useMeStore((s) => s.isInConservativeCountry)
  const onboardingData = useMeStore((s) => s.onboardingData)
  const {productLine} = useProductLine()

  // Helper function to get dynamic pregnancy date header title
  const getPregnancyDateHeaderTitle = useCallback(() => {
    if (onboardingData.pregnancyPostpartumStatus === 'pregnant') {
      return t('setup_profile_routes_whats_your_due_date')
    } else if (onboardingData.pregnancyPostpartumStatus === 'postpartum') {
      return t('setup_profile_routes_what_day_give_birth')
    }
    // Default fallback
    return t('setup_profile_routes_whats_your_due_date')
  }, [t, onboardingData.pregnancyPostpartumStatus])

  // Helper function to get dynamic pregnancy date description
  const getPregnancyDateDescription = useCallback(() => {
    if (onboardingData.pregnancyPostpartumStatus === 'pregnant') {
      return t(
        'set_up_profile_hooks_setup_profile_routes_sharing_your_due_date_helps'
      )
    } else if (onboardingData.pregnancyPostpartumStatus === 'postpartum') {
      return t(
        'set_up_profile_hooks_setup_profile_routes_sharing_day_you_gave_birth'
      )
    }
    // Default fallback
    return t(
      'set_up_profile_hooks_setup_profile_routes_sharing_your_due_date_helps'
    )
  }, [t, onboardingData.pregnancyPostpartumStatus])

  const routes = useMemo((): SetupProfileSubPropsSubRoute[] => {
    // Helper function to create routes with proper typing
    const createFertilityRoutes = (): SetupProfileSubPropsSubRoute[] => {
      const routesWithNulls: (SetupProfileSubPropsSubRoute | null)[] = [
        {
          path: '/birth-assigned-sex',
          component: BirthAssignedSex,
          showProgressBar: true,
          headerTitle: isInConservativeCountry()
            ? t('setup_profile_routes_what_is_your_gender')
            : t('setup_profile_routes_sex_assigned_at_birth'),
        },
        isInConservativeCountry()
          ? null
          : {
              path: '/gender-identity',
              component: GenderIdentity,
              headerTitle: t('setup_profile_routes_how_do_you_identify'),
            },
        {
          path: '/relationship-dynamic',
          component: RelationshipDynamic,
          headerTitle: t('setup_profile_routes_who_planning_build_family_with'),
        },
        {
          path: '/family-forming-journey',
          component: FamilyFormingJourney,
          headerTitle: t('setup_profile_routes_which_path_exploring'),
        },
        {
          path: '/complete',
          component: Complete,
          showProgressBar: false,
        },
      ]

      return routesWithNulls.filter(
        (route): route is SetupProfileSubPropsSubRoute => route !== null
      )
    }

    const createMenopauseRoutes = (): SetupProfileSubPropsSubRoute[] => {
      const routesWithNulls: (SetupProfileSubPropsSubRoute | null)[] = [
        {
          path: '/menstrual-period',
          component: MenstrualPeriod,
          showProgressBar: true,
          headerTitle: t('setup_profile_routes_having_menstrual_periods'),
        },
        {
          path: '/last-menstrual',
          component: LastMenstrual,
          showProgressBar: true,
          headerTitle: t('setup_profile_routes_when_last_menstrual_period'),
        },
        {
          path: '/menstrual-symptoms',
          component: MenopauseSymptoms,
          headerTitle: t(
            'set_up_profile_hooks_setup_profile_routes_have_you_noticed_any_of'
          ),
        },
        {
          path: '/complete',
          component: Complete,
          showProgressBar: false,
        },
      ]

      return routesWithNulls.filter(
        (route): route is SetupProfileSubPropsSubRoute => route !== null
      )
    }

    const createPregnancyRoutes = (): SetupProfileSubPropsSubRoute[] => {
      const routesWithNulls: (SetupProfileSubPropsSubRoute | null)[] = [
        {
          path: '/pregnancy-info',
          component: PregnancyInfo,
          showProgressBar: true,
          headerTitle: t('setup_profile_routes_which_best_applies'),
          description: t('setup_profile_routes_you_can_always_adjust'),
        },
        {
          path: '/pregnancy-date',
          component: PregnancyDate,
          headerTitle: getPregnancyDateHeaderTitle(),
          description: getPregnancyDateDescription(),
        },
        {
          path: '/complete',
          component: Complete,
          showProgressBar: false,
        },
      ]

      return routesWithNulls.filter(
        (route): route is SetupProfileSubPropsSubRoute => route !== null
      )
    }

    // Base routes for fertility (existing)
    const fertilityRoutes = createFertilityRoutes()

    // Routes for pregnancy (copy of fertility for now)
    const pregnancyRoutes = createPregnancyRoutes()

    // Routes for menopause (copy of fertility for now)
    const menopauseRoutes = createMenopauseRoutes()

    // Return routes based on product line
    switch (productLine) {
      case PREGNANCY_AND_POSTPARTUM:
        return pregnancyRoutes
      case MENOPAUSE:
        return menopauseRoutes
      case FERTILITY:
      default:
        return fertilityRoutes
    }
  }, [
    t,
    isInConservativeCountry,
    productLine,
    getPregnancyDateHeaderTitle,
    getPregnancyDateDescription,
  ])

  const getSubRoute = (
    index: SetupProfileRoutes
  ): SetupProfileSubPropsSubRoute => routes[index]

  const getSubRoutePath = (index: SetupProfileRoutes): string =>
    getSubRoute(index).path

  const navigateTo = (index: SetupProfileRoutes, baseUrl: string): void => {
    history.push(`${baseUrl}${getSubRoutePath(index)}`)
  }

  return {routes, getSubRoute, getSubRoutePath, navigateTo}
}

export default useSetupProfileRoutes
